import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, XCircle, ArrowRight, Spinner } from "@phosphor-icons/react";
import api, { formatApiError, errContext } from "../lib/api";
import TapTrustBadge from "../components/TapTrustBadge";

/**
 * BillingReturn — landing page Tap redirects to after a hosted checkout.
 * The URL contains ?tap_id=<charge_id> (Tap's default) or ?charge_id=<id>.
 * We poll GET /api/tap/charges/{id} until we see CAPTURED / FAILED / DECLINED.
 */
export default function BillingReturnPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const chargeId = params.get("tap_id") || params.get("charge_id");
  const [status, setStatus] = useState("checking"); // checking | ok | failed
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!chargeId) { setStatus("failed"); setError("Missing charge id in URL"); return; }
    let mounted = true;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/tap/charges/${chargeId}`);
        if (!mounted) return;
        setDetail(data);
        if (data.status === "CAPTURED") {
          setStatus("ok");
          toast.success("Payment complete — your account is updated.");
          return;
        }
        if (["FAILED", "DECLINED", "ABANDONED", "CANCELLED", "VOID"].includes(data.status)) {
          setStatus("failed");
          setError(`Payment ${data.status.toLowerCase()}`);
          return;
        }
        // still pending — retry up to 8 times over ~20 seconds
        if (attempts++ < 8) setTimeout(poll, 2500);
        else { setStatus("failed"); setError("Payment did not confirm in time. Check the Payments panel."); }
      } catch (err) {
        if (!mounted) return;
        setStatus("failed");
        setError(formatApiError(err.response?.data?.detail, errContext(err)) || "Failed to verify charge");
      }
    };
    poll();
    return () => { mounted = false; };
  }, [chargeId]);

  const goForward = () => {
    const intent = detail?.metadata?.intent;
    if (intent === "seats") navigate("/billing/seats");
    else if (intent === "card_update") navigate("/org");
    else navigate("/org");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 bg-grid px-4 py-8">
      <div className="w-full max-w-md bg-white border border-zinc-950 p-8" data-testid="billing-return-page">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-2">/ TAP · PAYMENT_RETURN</div>
        {status === "checking" && (
          <>
            <div className="w-14 h-14 bg-zinc-100 flex items-center justify-center mb-4 animate-spin">
              <Spinner size={28} className="text-zinc-950" />
            </div>
            <h2 className="font-display text-3xl font-black tracking-tight">Verifying payment…</h2>
            <p className="text-sm text-zinc-500 mt-2">Confirming with Tap. Hold on a second — do not close this tab.</p>
            <div className="mt-6 font-mono text-[10px] text-zinc-400">charge: {chargeId}</div>
          </>
        )}
        {status === "ok" && (
          <>
            <div className="w-14 h-14 bg-emerald-100 border border-emerald-600 flex items-center justify-center mb-4">
              <CheckCircle size={28} weight="fill" className="text-emerald-700" />
            </div>
            <h2 className="font-display text-3xl font-black tracking-tight">Payment complete.</h2>
            <p className="text-sm text-zinc-600 mt-2">
              We captured <strong>{detail?.currency} {detail?.amount}</strong> and updated your account.
            </p>
            <button
              onClick={goForward}
              data-testid="billing-return-forward-btn"
              className="mt-6 w-full bg-zinc-950 text-white font-semibold text-sm py-3 hover:bg-[#FF3E00] transition-colors flex items-center justify-center gap-2">
              CONTINUE <ArrowRight size={14} weight="bold" />
            </button>
            <div className="mt-4 flex justify-center">
              <TapTrustBadge />
            </div>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="w-14 h-14 bg-red-50 border border-[#D0021B] flex items-center justify-center mb-4">
              <XCircle size={28} weight="fill" className="text-[#D0021B]" />
            </div>
            <h2 className="font-display text-3xl font-black tracking-tight">Payment did not complete.</h2>
            <p className="text-sm text-zinc-600 mt-2" data-testid="billing-return-error">{error || "Please try again."}</p>
            <div className="mt-6 flex gap-2">
              <Link to="/billing/seats" className="flex-1 border border-zinc-950 text-center font-semibold text-sm py-3 hover:bg-zinc-950 hover:text-white transition-colors">RETRY</Link>
              <Link to="/org" className="flex-1 border border-zinc-300 text-center font-semibold text-sm py-3 hover:bg-zinc-100 transition-colors">BACK TO ORG</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
